import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setSeo, setJsonLd, setRobots, setHreflang } from "@/lib/seo";
import { SEO_BASE, PRIVATE_RE, seoDefaultsFor, humanize } from "@/lib/seoRoutes";

const removeJsonLd = (id) => document.getElementById(`jsonld-${id}`)?.remove();
const FALLBACK_TITLE = seoDefaultsFor("/__none__").title;
let _redirects = null;
const loadRedirects = () => _redirects || (_redirects = fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/seo/redirects`).then((r) => (r.ok ? r.json() : {})).catch(() => ({})));

// Runs before page effects on every navigation: canonical/og:url, robots, default meta, breadcrumbs, slash normalisation.
export const SeoGuard = () => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (pathname.length > 1 && pathname.endsWith("/")) {
      navigate(pathname.replace(/\/+$/, "") + search + hash, { replace: true });
      return;
    }
    const path = pathname || "/";
    let cancelled = false;
    loadRedirects().then((map) => {
      const to = map && map[path];
      if (cancelled || !to || to === path) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/seo/redirects/hit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ from: path }), keepalive: true }).catch(() => {});
      navigate(to + search, { replace: true });
    });
    const isPrivate = PRIVATE_RE.test(path) || !!window.__gfBasename;
    document.documentElement.lang = path === "/hi" || path.startsWith("/hi/") ? "hi" : "en";
    if (!/^\/(hi\/)?(coreservices|insights|podcast)\/[^/]+$/.test(path) && !["/", "/hi", "/marketpulse", "/hi/marketpulse"].includes(path)) setHreflang([]);
    setRobots(isPrivate ? "noindex, nofollow" : "index, follow");
    if (isPrivate) {
      document.title = "Growthifye";
      const link = document.head.querySelector('link[rel="canonical"]');
      if (link) link.href = SEO_BASE + path;
      removeJsonLd("breadcrumbs"); removeJsonLd("dataset");
      return;
    }
    const meta = seoDefaultsFor(path);
    setSeo(meta.title, meta.description);
    const segs = path.split("/").filter(Boolean);
    if (segs.length) {
      const items = [{ "@type": "ListItem", position: 1, name: "Home", item: SEO_BASE + "/" }];
      segs.forEach((s, i) => {
        const p = "/" + segs.slice(0, i + 1).join("/");
        const m = seoDefaultsFor(p);
        const name = m.title !== FALLBACK_TITLE && i < segs.length - 1 ? m.title.split(/ [—|] /)[0] : humanize(s);
        items.push({ "@type": "ListItem", position: i + 2, name, item: SEO_BASE + p });
      });
      setJsonLd("breadcrumbs", { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items });
    } else removeJsonLd("breadcrumbs");
    if (meta.dataset) {
      setJsonLd("dataset", { "@context": "https://schema.org", "@type": "Dataset", name: meta.dataset, description: meta.description, url: SEO_BASE + path,
        creator: { "@type": "Organization", name: "Growthifye", url: SEO_BASE }, license: SEO_BASE + "/terms", isAccessibleForFree: true, spatialCoverage: "India" });
    } else removeJsonLd("dataset");
    return () => { cancelled = true; };
  }, [pathname, search, hash, navigate]);
  return null;
};
